/**
 * Règles de mot de passe, source unique côté client.
 *
 * Un SEUL module pour les deux usages : l'indicateur en direct (`PasswordPolicy.tsx`) et la
 * validation bloquante des formulaires (`schemas.ts`). Les deux ont divergé par le passé —
 * la liste des motifs interdits était affichée mais pas bloquante, si bien qu'on pouvait
 * passer à l'étape suivante avec une règle visiblement non remplie, pour se faire refuser
 * par le serveur à la toute fin.
 *
 * Ces règles DUPLIQUENT celles du serveur (`PasswordPolicy.cs`), qui reste la seule barrière
 * réelle : ici c'est du confort (retour immédiat, sans aller-retour réseau). Les deux copies
 * doivent rester alignées — seuils, liste et formulation des messages compris.
 */

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Alignée au caractère près sur `Blocklist` côté C# (`PasswordPolicy.cs`). */
export const PASSWORD_BLOCKLIST = [
  'motdepasse', 'password', 'azertyuiop', 'qwertyuiop', '123456789012',
  'administrateur', 'restaurant', 'pixlide', 'bienvenue', 'changeme',
];

/** Mot de la liste interdite trouvé dans le mot de passe, ou `null` si aucun ne correspond. */
export function matchedBlockedWord(password: string): string | null {
  const normalized = password.toLowerCase();

  return PASSWORD_BLOCKLIST.find((blocked) => normalized.includes(blocked)) ?? null;
}

export interface PasswordRule {
  /** Clé stable pour React : `label` varie avec la saisie, ne peut pas servir de `key`. */
  id: string;
  /** Fonction et non chaîne fixe : la règle « pas de mot interdit » nomme le mot trouvé. */
  label: (password: string, email: string) => string;
  isSatisfied: (password: string, email: string) => boolean;
  /**
   * Message d'erreur du formulaire quand la règle n'est pas remplie. Distinct du `label`,
   * qui est formulé au positif (« Au moins 12 caractères ») pour une liste à cocher.
   */
  error: (password: string, email: string) => string;
}

/**
 * Règle « ne contient pas l'adresse email » : dépend de l'email, donc évaluée uniquement là
 * où il est disponible (formulaire d'inscription complet), et neutre partout ailleurs.
 */
function containsEmailLocalPart(password: string, email: string): boolean {
  const localPart = email.split('@')[0]?.toLowerCase() ?? '';

  return localPart.length >= 4 && password.toLowerCase().includes(localPart);
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'min-length',
    label: () => `Au moins ${PASSWORD_MIN_LENGTH} caractères`,
    isSatisfied: (password) => password.length >= PASSWORD_MIN_LENGTH,
    error: () => `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères.`,
  },
  {
    id: 'max-length',
    label: () => `Pas plus de ${PASSWORD_MAX_LENGTH} caractères`,
    isSatisfied: (password) => password.length <= PASSWORD_MAX_LENGTH,
    error: () => `Le mot de passe ne peut pas dépasser ${PASSWORD_MAX_LENGTH} caractères.`,
  },
  {
    id: 'variety',
    label: () => 'Au moins 5 caractères différents',
    isSatisfied: (password) => new Set(password).size >= 5,
    error: () => 'Le mot de passe doit contenir au moins 5 caractères différents.',
  },
  {
    id: 'no-email',
    label: () => 'Ne contient pas votre adresse email',
    isSatisfied: (password, email) => !containsEmailLocalPart(password, email),
    error: () => 'Le mot de passe ne doit pas contenir votre adresse email.',
  },
  {
    id: 'no-blocked-word',
    // Nomme le mot trouvé plutôt qu'un « trop courant » générique, exactement comme le
    // message renvoyé par le serveur (`PasswordPolicy.cs`, code `password.contains_blocked_word`)
    // — sans ça, un rejet semblerait arbitraire alors qu'il était visible en direct sous
    // une autre formulation.
    label: (password) => {
      const blocked = matchedBlockedWord(password);

      return blocked !== null ? `Ne contient pas « ${blocked} »` : 'Ne contient pas un mot trop courant';
    },
    isSatisfied: (password) => matchedBlockedWord(password) === null,
    error: (password) => {
      const blocked = matchedBlockedWord(password);

      return blocked !== null
        ? `Le mot de passe ne doit pas contenir « ${blocked} ».`
        : 'Ce mot de passe est trop courant. Choisissez plutôt une phrase de passe.';
    },
  },
];

/**
 * Première règle non remplie, ou `null` si toutes le sont. Le formulaire n'affiche qu'un
 * message à la fois sous le champ : la liste à cocher, elle, montre déjà l'état complet.
 */
export function firstUnsatisfiedRule(password: string, email: string): PasswordRule | null {
  return PASSWORD_RULES.find((rule) => !rule.isSatisfied(password, email)) ?? null;
}
